"use client";

import { FocusEvent, forwardRef, useEffect, useRef, useState } from "react";

import { CalendarMonthIcon, ClockIcon } from "@saas/shared/icon";
import { classNames } from "@saas/shared/utils";

import BaseInput, { BaseInputProps } from "../base-input/base-input";
import Typography from "../typography/typography";

export interface DateInputProps extends BaseInputProps {
  type: "date" | "time" | "datetime-local";
  inputClassName?: string;
  label: string;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      type,
      label,
      className,
      showLabel = true,
      inputClassName,
      ...props
    }: DateInputProps,
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState<boolean>(false);

    const hasValue =
      !!inputRef.current?.value.length ||
      !!inputRef.current?.defaultValue.length ||
      !!props.value;

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore firefox issue
        e.nativeEvent.explicitOriginalTarget &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore firefox issue
        e.nativeEvent.explicitOriginalTarget === e.nativeEvent.originalTarget
      ) {
        return;
      }

      setFocus(false);
      props.onBlur?.(e);
      props.onChange?.(e);
    };

    useEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(inputRef.current);
        } else {
          ref.current = inputRef.current;
        }
      }
    });

    return (
      <div className={classNames("relative", className)}>
        <BaseInput
          {...props}
          className={inputClassName}
          type={type}
          onFocus={handleFocus}
          onBlur={handleBlur}
          label={label}
          showLabel={false}
          ref={inputRef}
          action={{
            icon: type === "time" ? ClockIcon : CalendarMonthIcon,
            onClick: () => inputRef.current?.focus(),
          }}
          disabled={props.disabled}
        />
        <Typography
          type={hasValue || focus ? "label-medium" : "body-b1"}
          className={classNames(
            "text-he bg-neutral-N0 z-1 pointer-events-none absolute left-4 flex items-center rounded-sm transition duration-100 ease-in-out",
            hasValue || focus
              ? "top-0 -translate-y-2 -translate-x-1 px-1"
              : "inset-y-1 w-[calc(100%-60px)]",
            !showLabel && "sr-only",
            props.hint && "mb-5",
            focus && props.error && "text-error",
            props.disabled && "text-disabled"
          )}
        >
          {label}
        </Typography>
      </div>
    );
  }
);

DateInput.displayName = "DateInput";

export default DateInput;
